import { useState, useEffect } from 'preact/hooks';
import { activeContactSignal, activeSummaryJobSignal } from '../../state/contacts.store';
import { apiFetch } from '../../services/api';
import { showToast } from '../../state/ui.store';
import { formatFullTooltipDateTime } from '../../utils/formatters';
import { Zap, MessageSquare, Calendar, Sparkles } from 'lucide-preact';

interface SummaryRangeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface RangeInfoResponse {
  totalMessages: number;
  startDate: string | null;
  endDate: string | null;
}

export function SummaryRangeModal({ isOpen, onClose }: SummaryRangeModalProps) {
  const contact = activeContactSignal.value;

  const [selectedCount, setSelectedCount] = useState<number>(50);
  const [dbTotalMessages, setDbTotalMessages] = useState<number>(50);
  const [startDateStr, setStartDateStr] = useState<string>('Buscando...');
  const [fetchingInfo, setFetchingInfo] = useState<boolean>(false);
  const [isStarting, setIsStarting] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen && contact) {
      fetchRangeInfo(selectedCount);
    }
  }, [isOpen, contact]);

  const fetchRangeInfo = async (count: number) => {
    if (!contact) return;
    try {
      setFetchingInfo(true);
      const res = await apiFetch<RangeInfoResponse>(`/messages/${contact.phone_number}/range-info?limit=${count}`);
      if (res) {
        const total = res.totalMessages || 1;
        setDbTotalMessages(total);
        if (res.startDate) {
          setStartDateStr(formatFullTooltipDateTime(new Date(res.startDate)));
        } else {
          setStartDateStr('Início das conversas salvas');
        }
      }
    } catch (err: any) {
      console.error('[SummaryRangeModal] Erro ao buscar intervalo:', err);
      setStartDateStr('Erro ao calcular intervalo');
    } finally {
      setFetchingInfo(false);
    }
  };

  const handleSliderChange = (count: number) => {
    setSelectedCount(count);
    fetchRangeInfo(count);
  };

  const handleGenerate = async () => {
    if (!contact) return;
    try {
      setIsStarting(true);
      const res = await apiFetch<any>(`/ai/summarize/${contact.phone_number}`, {
        method: 'POST',
        body: JSON.stringify({ limitMessages: selectedCount })
      });

      if (res?.job) {
        activeSummaryJobSignal.value = res.job;
        showToast(`Job de síntese iniciado para as últimas ${selectedCount} mensagens!`, 'success');
        onClose();
      }
    } catch (err: any) {
      showToast(`Erro ao iniciar síntese: ${err.message}`, 'error');
    } finally {
      setIsStarting(false);
    }
  };

  if (!isOpen || !contact) return null;

  return (
    <div class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in">
      <div class="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-6">
        
        {/* Header */}
        <div class="flex items-center justify-between border-b border-slate-800 pb-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-2xl bg-cyan-500/20 border border-cyan-400/40 flex items-center justify-center text-cyan-300">
              <Sparkles class="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 class="text-base font-bold text-slate-100">Gerar Síntese Executiva</h3>
              <p class="text-xs text-slate-400">Selecione a quantidade de mensagens para resumir</p>
            </div>
          </div>
          <button
            onClick={onClose}
            class="text-slate-400 hover:text-slate-200 text-sm p-1.5 rounded-xl hover:bg-slate-800 transition cursor-pointer"
          >
            ✕
          </button>
        </div>

        {/* Presets */}
        <div class="space-y-2">
          <label class="text-[11px] font-bold text-slate-300 uppercase tracking-wider block flex items-center gap-1.5">
            <Zap class="w-4 h-4 text-cyan-400" />
            <span>Atalhos Rápidos:</span>
          </label>
          <div class="flex flex-wrap items-center gap-2">
            {[50, 200, 500].map(cnt => (
              <button
                key={cnt}
                type="button"
                onClick={() => handleSliderChange(Math.min(cnt, dbTotalMessages))}
                class={`px-3 py-1.5 rounded-xl border text-xs font-semibold transition cursor-pointer ${
                  selectedCount === cnt
                    ? 'bg-cyan-600/30 border-cyan-400/60 text-cyan-200 shadow-sm font-bold'
                    : 'bg-slate-800 hover:bg-cyan-950/60 border-slate-700 hover:border-cyan-500/50 text-slate-200'
                }`}
              >
                Últimas {cnt} msgs
              </button>
            ))}
            <button
              type="button"
              onClick={() => handleSliderChange(dbTotalMessages)}
              class={`px-3 py-1.5 rounded-xl border text-xs font-semibold transition cursor-pointer ${
                selectedCount === dbTotalMessages
                  ? 'bg-cyan-600/30 border-cyan-400/60 text-cyan-200 shadow-sm font-bold'
                  : 'bg-slate-800 hover:bg-cyan-950/60 border-slate-700 hover:border-cyan-500/50 text-slate-200'
              }`}
            >
              Todo o Histórico ({dbTotalMessages})
            </button>
          </div>
        </div>

        {/* Range Slider */}
        <div class="p-4 bg-slate-950/90 rounded-2xl border border-slate-800 space-y-3.5 shadow-inner">
          <div class="flex items-center justify-between">
            <span class="text-xs font-bold text-cyan-300 flex items-center gap-1.5">
              <MessageSquare class="w-4 h-4 text-cyan-400" />
              <span>Quantidade de Mensagens</span>
            </span>
            <span class="text-xs font-mono font-bold px-2.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-400/30">
              {selectedCount} de {dbTotalMessages} mensagens (Banco de Dados)
            </span>
          </div>

          <input
            type="range"
            min={1}
            max={dbTotalMessages}
            value={selectedCount}
            onInput={(e) => handleSliderChange(parseInt((e.target as HTMLInputElement).value, 10))}
            class="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-cyan-400 hover:accent-cyan-300 focus:outline-none"
          />

          <div class="flex items-center justify-between text-[11px] text-slate-400 font-mono pt-1 border-t border-slate-800/60">
            <span>1 msg (Mais recente)</span>
            <span>{dbTotalMessages} msgs (Total no Banco)</span>
          </div>
        </div>

        {/* Start Date Feedback */}
        <div class="p-3 bg-slate-950/60 rounded-xl border border-slate-800/80 space-y-1">
          <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <Calendar class="w-3.5 h-3.5 text-cyan-400" />
            <span>Data Inicial (A partir de):</span>
          </span>
          <p class="text-xs font-mono font-medium text-cyan-200 truncate">
            {fetchingInfo ? 'Calculando intervalo no banco...' : startDateStr}
          </p>
        </div>

        {/* Footer */}
        <div class="flex items-center justify-end gap-3 pt-2 border-t border-slate-800">
          <button
            type="button"
            onClick={onClose}
            class="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl transition cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isStarting}
            class="px-5 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-cyan-600/30 transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            ✨ {isStarting ? 'Solicitando...' : `Gerar Resumo com ${selectedCount} Mensagens`}
          </button>
        </div>
      </div>
    </div>
  );
}
